import { useRef, useState } from "react";

import { Callout, Classes, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { PenLine } from "lucide-react";
import { useParams } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { useAuth } from "@/components/lib/auth";
import { ScenarioHeader } from "@/components/pages_logged_in/roleplays/details/ScenarioHeader";
import { EditScenario } from "@/components/pages_logged_in/roleplays/details/scenario/edit/EditScenario";
import { ScenarioCollections } from "@/components/pages_logged_in/roleplays/details/scenario/ScenarioCollections";
import { TagsList } from "@/components/pages_logged_in/roleplays/TagList";
import { Container } from "@/components/pieces/bootstrap";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { CardAboveXs } from "@/components/pieces/CardAboveXs";
import { EvaluationCriteriaManager } from "@/components/pieces/evaluation-criteria-manager/EvaluationCriteriaManager";
import { IconCircle } from "@/components/pieces/IconCircle";
import { formatBytes } from "@/components/pieces/roleplays/utils";
import { SmartLinkMarkdown } from "@/components/pieces/SmartLinkMarkdown";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatFileType } from "@/components/ui/file-list";
import { PDFViewer } from "@/components/ui/pdf-viewer";
import {
  CTA_COLOR,
  LIGHT_BLUE,
  SANS_SERIF_FONT,
  SIDE_NAV_WIDTH,
} from "@/css/constants";
import { TagType } from "@/graphql";
import { FullRoleplay, FullScenario } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

import { HowToRoleplay } from "./HowToRoleplay";
import { Notes } from "./Notes";
import { PlayScenarioCard } from "./PlayScenarioCard";
import { ScenarioAssignmentInfo } from "./ScenarioAssignmentInfo";

function ScenarioOverview({
  scenario,
  onEditScenario,
}: {
  scenario: FullScenario;
  onEditScenario: () => void;
}) {
  return (
    <Box>
      {scenario.context ? (
        <Box>
          <div className="flex items-center justify-between">
            <Flex fontWeight={600} fontSize={18} alignItems="center">
              <IconCircle
                icon="book"
                size={30}
                backgroundColor="#E5F2FF"
                color={CTA_COLOR}
              />
              <Box ml={2}>Scenario Context</Box>
            </Flex>{" "}
            {scenario.canEdit && (
              <Button
                variant="minimal"
                intent="secondary"
                onClick={onEditScenario}
                size="icon"
              >
                <PenLine />
              </Button>
            )}
          </div>
          <Box mt={2} className={Classes.RUNNING_TEXT}>
            <StyledMarkdown>{scenario.context}</StyledMarkdown>
          </Box>
        </Box>
      ) : null}

      {scenario.objective ? (
        <Box mt={4}>
          <Flex fontWeight={600} fontSize={18} alignItems="center">
            <IconCircle
              icon="endorsed"
              size={30}
              backgroundColor={LIGHT_BLUE}
              color={CTA_COLOR}
            />
            <Box ml={2}>Your Objective</Box>
          </Flex>
          <Box mt={2}>
            <StyledMarkdown className={Classes.RUNNING_TEXT}>
              {scenario.objective}
            </StyledMarkdown>
          </Box>
        </Box>
      ) : null}

      {scenario.presentations.length > 0 ? (
        <ScenarioPresentationsView presentations={scenario.presentations} />
      ) : null}
    </Box>
  );
}

function ScenarioPresentationsView(props: {
  presentations: FullScenario["presentations"];
}) {
  return (
    <Box mt={4}>
      <Flex fontWeight={600} fontSize={18} alignItems="center">
        <IconCircle
          icon="endorsed"
          size={30}
          backgroundColor={LIGHT_BLUE}
          color={CTA_COLOR}
        />
        <Box ml={2}>Presentation Materials</Box>
      </Flex>
      <Box mt={2}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-hidden">
          {props.presentations.map((presentation) => (
            <div
              onClick={() => window.open(presentation.file.url, "_blank")}
              className="bg-white hover:bg-blue-100 cursor-pointer p-2 rounded-md border-border border overflow-hidden"
            >
              <div className="rounded-sm bg-black grid h-42 items-center overflow-hidden">
                <PDFViewer url={presentation.file.url} pageNumber={1} />
              </div>
              <div className="grid gap-1 mt-2">
                <div className="text-gray-800 text-xs font-medium truncate">
                  {presentation.file.name}
                </div>
                <div className="justify-start text-gray-500 text-[10px] font-medium">
                  {formatFileType(presentation.file.contentType ?? "")}{" "}
                  {formatBytes(presentation.file.size, 0)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Box>
    </Box>
  );
}

function TagsListContent({
  tags,
  onAddClick,
  canEdit,
}: {
  tags: TagType[];
  onAddClick: () => void;
  canEdit: boolean;
}) {
  const isEmpty = tags.length === 0;

  if (isEmpty && !canEdit) {
    return null;
  }

  return (
    <Box mt={4} className="border-border-1 rounded-md border bg-white p-4">
      <div className="flex items-center justify-between">
        <Flex fontWeight={600} fontSize={20} mb={3} alignItems="center">
          <IconCircle icon="tag" size={32} mr={2} />
          <div>Tags</div>
        </Flex>
        {!isEmpty && canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <div className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-purple-50 p-1">
                <Icon icon="edit" size={20} />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onAddClick}>
                Edit Tags
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div>
        <TagsList tags={tags} onAddClick={onAddClick} />
      </div>
    </Box>
  );
}

const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
  if (ref.current) {
    const element = ref.current;
    const y = element.getBoundingClientRect().top + window.scrollY - 76;

    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

interface ScenarioContentInnerProps {
  scenario: FullScenario;
  startScenarioRef: React.RefObject<HTMLDivElement | null>;
  onEditCharacter: () => void;
  onEditScenario: () => void;
}

function ScenarioContentInner({
  scenario,
  startScenarioRef,
  onEditCharacter,
  onEditScenario,
}: ScenarioContentInnerProps) {
  const { roleplaySlug } = useParams();
  const { currentWorkspace } = useAuth();
  const allowedEditTags = !currentWorkspace.isPersonal;
  const [isEditScenarioDrawerOpen, setIsEditScenarioDrawerOpen] = useState<
    "basic" | "character" | "scenario" | null
  >(null);
  const evaluationComponent = (
    <EvaluationCriteriaManager
      scenarioId={scenario?.id}
      canEdit={scenario?.canEdit ?? false}
    />
  );

  return (
    <Box mb={100}>
      <EditScenario
        initialTab={isEditScenarioDrawerOpen}
        onClose={() => setIsEditScenarioDrawerOpen(null)}
        scenario={scenario}
      />
      <Box id="start-sidebar" ref={startScenarioRef}>
        <div style={{ marginBottom: 24 }}>
          <PlayScenarioCard
            scenario={scenario}
            roleplaySlug={roleplaySlug}
            onEditCharacter={onEditCharacter}
            onEditScenario={onEditScenario}
          />
        </div>
      </Box>
      <Box>
        <CardAboveXs>
          <ScenarioOverview
            scenario={scenario}
            onEditScenario={onEditScenario}
          />
          <Box mt={4}>
            <Notes scenario={scenario} />
          </Box>
        </CardAboveXs>
        <Media greaterThan="sm">
          <Box mt={24}>{evaluationComponent}</Box>
          <Box mt={24}>
            <ScenarioCollections scenario={scenario} />
          </Box>
          <Box mt={24}>
            <TagsListContent
              tags={scenario.tags as TagType[]}
              onAddClick={() => {
                setIsEditScenarioDrawerOpen("basic");
              }}
              canEdit={!!scenario?.canEdit && allowedEditTags}
            />
          </Box>
        </Media>
      </Box>
      <Media lessThan="md">
        <Box className="bts" my={4} />
        {evaluationComponent}
      </Media>
      <Media lessThan="md">
        <Box className="bts" my={4} />
        <ScenarioCollections scenario={scenario} />
      </Media>
      <Media lessThan="md">
        <Box className="bts" my={4} />
        <TagsListContent
          canEdit={!!scenario?.canEdit && allowedEditTags}
          tags={scenario.tags as TagType[]}
          onAddClick={() => {
            setIsEditScenarioDrawerOpen("basic");
          }}
        />
      </Media>
    </Box>
  );
}

interface ScenarioContentProps {
  scenario?: FullScenario;
  showIntro: boolean;
  loading: boolean;
  roleplay?: FullRoleplay;
  isBareLayout?: boolean;
}

export function ScenarioContent({
  scenario,
  showIntro = false,
  loading = false,
  roleplay,
  isBareLayout = false,
}: ScenarioContentProps) {
  const evaluationCriteria = ensureNonNullArray(scenario?.evaluationCriteria);
  const startScenarioRef = useRef<HTMLDivElement>(null);
  const { roleplaySlug } = useParams();
  const isScenarioDetailPage = !roleplaySlug;
  const [editScenarioTab, setEditScenarioTab] = useState<
    "basic" | "character" | "scenario" | null
  >(null);

  return (
    <Box ml={isBareLayout ? 0 : [0, 0, SIDE_NAV_WIDTH]}>
      <ScenarioHeader
        page={"scenarioDetail"}
        roleplay={isScenarioDetailPage ? undefined : roleplay}
        scenario={scenario}
        loading={loading}
        onEditScenario={() => setEditScenarioTab("basic")}
      />
      {scenario?.id && <ScenarioAssignmentInfo scenarioId={scenario.id} />}
      <Media greaterThan="md">
        <HowToRoleplay show={showIntro} />
      </Media>
      <Media lessThan="md">
        <Box p={3} backgroundColor="#FFF" className="bbs">
          <FlexibleButton
            icon="arrow-down"
            size="small"
            onClick={() => scrollToRef(startScenarioRef)}
          >
            Jump to Start
          </FlexibleButton>
        </Box>
      </Media>
      <Container>
        <Box mt={[24, 36]}>
          {loading ? (
            <>
              <Box className={Classes.SKELETON} height={350} />
              <Box className={Classes.SKELETON} height={350} mt={24} />
            </>
          ) : (
            <>
              {(evaluationCriteria?.length === 0 ||
                evaluationCriteria?.every(
                  (criterion) => criterion.items?.length === 0,
                )) && (
                <Callout intent="warning" style={{ marginBottom: 16 }}>
                  This scenario does not have any evaluation criteria items. You
                  will not be able to start the session.
                </Callout>
              )}
              {scenario && (
                <ScenarioContentInner
                  scenario={scenario}
                  startScenarioRef={startScenarioRef}
                  onEditCharacter={() => setEditScenarioTab("character")}
                  onEditScenario={() => setEditScenarioTab("scenario")}
                />
              )}
            </>
          )}
        </Box>
      </Container>
      {scenario && editScenarioTab && (
        <EditScenario
          key={`${scenario.id}-${editScenarioTab}`}
          initialTab={editScenarioTab}
          scenario={scenario}
          onClose={() => setEditScenarioTab(null)}
        />
      )}
    </Box>
  );
}

const StyledMarkdown = styled(SmartLinkMarkdown)`
  line-height: 1.6;

  ul li:not(:last-child) {
    margin-bottom: 12px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: ${SANS_SERIF_FONT};
    margin-bottom: 10px !important;
    margin-top: 24px !important;
  }
`;
